import Axios from 'axios';
import {
  Box, Paper, Typography,
  Table, TableBody, TableCell, TableHead, TableRow,
  Tabs, Tab, Button, CircularProgress, IconButton,
  Modal, Backdrop, Fade, TextField
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import CloseIcon from '@material-ui/icons/Close';
import React, { useEffect, useRef, useState } from 'react';
import swal from 'sweetalert';
import { config } from '../constants';

const STANDARD_BANK_SESSION_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds

// Save session to localStorage
const saveStandardBankSession = (profileId, status, inputPrompt) => {
  const sessionKey = `standardBankSession_${profileId}`;
  localStorage.setItem(sessionKey, JSON.stringify({
    status,
    inputPrompt: inputPrompt || "",
    startedAt: Date.now()
  }));
};

// Load session from localStorage
const loadStandardBankSession = (profileId) => {
  const sessionKey = `standardBankSession_${profileId}`;
  const savedSession = localStorage.getItem(sessionKey);
  if (!savedSession) return null;

  try {
    const session = JSON.parse(savedSession);
    // Check if session is expired (older than 10 minutes)
    if (Date.now() - session.startedAt > STANDARD_BANK_SESSION_TIMEOUT) {
      localStorage.removeItem(sessionKey);
      return null;
    }
    return session;
  } catch (error) {
    localStorage.removeItem(sessionKey);
    return null;
  }
};

// Clear session from localStorage
const clearStandardBankSession = (profileId) => {
  const sessionKey = `standardBankSession_${profileId}`;
  localStorage.removeItem(sessionKey);
};

// FNB Session Management
const FNB_SESSION_TIMEOUT = 10 * 60 * 1000; // 10 minutes
const FNB_POLLING_INTERVAL = 3000; // 3 seconds

const saveFnbSession = (profileId, status, inputPrompt) => {
  const sessionKey = `fnbSession_${profileId}`;
  localStorage.setItem(sessionKey, JSON.stringify({
    status,
    inputPrompt: inputPrompt || "",
    startedAt: Date.now()
  }));
};

const loadFnbSession = (profileId) => {
  const sessionKey = `fnbSession_${profileId}`;
  const savedSession = localStorage.getItem(sessionKey);
  if (!savedSession) return null;

  try {
    const session = JSON.parse(savedSession);
    if (Date.now() - session.startedAt > FNB_SESSION_TIMEOUT) {
      localStorage.removeItem(sessionKey);
      return null;
    }
    return session;
  } catch (error) {
    localStorage.removeItem(sessionKey);
    return null;
  }
};

const clearFnbSession = (profileId) => {
  const sessionKey = `fnbSession_${profileId}`;
  localStorage.removeItem(sessionKey);
};

// Nedbank Session Management
const NEDBANK_SESSION_TIMEOUT = 10 * 60 * 1000; // 10 minutes

const saveNedbankSession = (profileId, status, inputPrompt) => {
  const sessionKey = `nedbankSession_${profileId}`;
  localStorage.setItem(sessionKey, JSON.stringify({
    status,
    inputPrompt: inputPrompt || "",
    startedAt: Date.now()
  }));
};

const loadNedbankSession = (profileId) => {
  const sessionKey = `nedbankSession_${profileId}`;
  const savedSession = localStorage.getItem(sessionKey);
  if (!savedSession) return null;

  try {
    const session = JSON.parse(savedSession);
    if (Date.now() - session.startedAt > NEDBANK_SESSION_TIMEOUT) {
      localStorage.removeItem(sessionKey);
      return null;
    }
    return session;
  } catch (error) {
    localStorage.removeItem(sessionKey);
    return null;
  }
};

const clearNedbankSession = (profileId) => {
  const sessionKey = `nedbankSession_${profileId}`;
  localStorage.removeItem(sessionKey);
};

// Check if any bank has an active session
const getActiveBankSession = (profileId) => {
  const standardBankSession = loadStandardBankSession(profileId);
  const nedbankSession = loadNedbankSession(profileId);
  const fnbSession = loadFnbSession(profileId);

  // Check if session is active (not terminal state)
  if (standardBankSession && !["approved", "failed"].includes(standardBankSession.status)) {
    return { bank: "Standard Bank", session: standardBankSession };
  }
  if (nedbankSession && !["approved", "failed"].includes(nedbankSession.status)) {
    return { bank: "Nedbank", session: nedbankSession };
  }
  if (fnbSession && !["approved", "failed"].includes(fnbSession.status)) {
    return { bank: "FNB Bank", session: fnbSession };
  }

  return null;
};

const StandardBankBlock = React.forwardRef(({ data, profileId, onRefresh, isBlocked, activeSession }, ref) => {
  const [activeTab, setActiveTab] = useState(0);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [standardBankStatus, setStandardBankStatus] = useState("initial");
  const [standardBankOtp, setStandardBankOtp] = useState("");
  const [standardBankOtpError, setStandardBankOtpError] = useState("");
  const [standardBankInputPrompt, setStandardBankInputPrompt] = useState("");
  const [shouldPoll, setShouldPoll] = useState(false);
  const [timeoutId, setTimeoutId] = useState(null);

  const POLLING_INTERVAL = 2000; // 2 seconds (faster than Nedbank's 10s)

  // Expose openModal and stopPolling functions to parent via ref
  React.useImperativeHandle(ref, () => ({
    openModal: () => {
      setShowStatusModal(true);
    },
    stopPolling: () => {
      console.log('🛑 [Standard Bank] Stopping polling from parent');
      setShouldPoll(false);
    }
  }));

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Status polling
  const pollStandardBankStatus = async (profile) => {
    console.log('🔄 [Standard Bank] Polling status for profile:', profile);
    try {
      const res = await Axios.get(
        `${config.BANK_API_URL}/api/standardBank/getStatus?profileId=${profile}`,
        {
          headers: {
            "X-Tenant-ID": "client-3"
          }
        }
      );
      const { status, inputPrompt } = res.data;
      console.log('🔄 [Standard Bank] Status response:', { status, inputPrompt });

      setStandardBankStatus(status);
      setStandardBankInputPrompt(inputPrompt || "");

      // Update localStorage with latest status
      saveStandardBankSession(profile, status, inputPrompt);

      // Stop polling on terminal states
      if (status === "approved" || status === "error" || status === "failed") {
        console.log('🔄 [Standard Bank] Terminal state reached:', status);
        setShouldPoll(false);

        // Clear session from localStorage
        clearStandardBankSession(profile);

        // Clear timeout on terminal state
        if (timeoutId) {
          clearTimeout(timeoutId);
          setTimeoutId(null);
        }

        // If approved, fetch data
        if (status === "approved") {
          console.log('🔄 [Standard Bank] Fetching final data');
          await fetchStandardBankData(profile);
        }
      }
    } catch (error) {
      console.error("🔴 [Standard Bank] Error polling status:", error);
    }
  };

  // Polling effect
  useEffect(() => {
    console.log('⚡ [Standard Bank] useEffect triggered. shouldPoll:', shouldPoll, 'profileId:', profileId);
    if (shouldPoll && profileId) {
      console.log('⚡ [Standard Bank] Starting polling interval (every', POLLING_INTERVAL, 'ms)');

      // Make the first poll immediately
      pollStandardBankStatus(profileId);

      // Then set up interval for subsequent polls
      const intervalId = setInterval(
        () => pollStandardBankStatus(profileId),
        POLLING_INTERVAL
      );
      return () => {
        console.log('⚡ [Standard Bank] Cleaning up interval');
        clearInterval(intervalId);
      };
    }
  }, [shouldPoll, profileId]);

  // Auto-resume session on component mount
  useEffect(() => {
    if (!profileId) return;

    const savedSession = loadStandardBankSession(profileId);

    if (savedSession) {
      console.log('🔄 [Standard Bank Admin Panel] Resuming previous session:', savedSession);

      // Restore the session state
      setStandardBankStatus(savedSession.status);
      setStandardBankInputPrompt(savedSession.inputPrompt);
      // Don't auto-open modal - let user click the banner
      // setShowStatusModal(true);

      // Only start polling if not in a terminal state
      if (!["approved", "error", "failed"].includes(savedSession.status)) {
        setShouldPoll(true);
      } else {
        // If terminal state, clear the session
        clearStandardBankSession(profileId);
      }
    }
  }, []); // Run only on mount

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  // OTP submission handler
  const handleOtpSubmit = async () => {
    if (!standardBankOtp || standardBankOtp.trim().length === 0) {
      setStandardBankOtpError("Please enter the OTP");
      return;
    }

    try {
      await Axios.post(
        `${config.BANK_API_URL}/api/standardBank/submitOtp`,
        {
          profileId: profileId,
          otp: standardBankOtp.trim()
        },
        {
          headers: {
            "X-Tenant-ID": "client-3"
          }
        }
      );

      setStandardBankOtp("");
      setStandardBankOtpError("");
      setStandardBankStatus("otp-submitted"); // Update UI immediately
      // Polling continues to get real status
    } catch (error) {
      setStandardBankOtpError(
        error.response?.data?.message ||
        "Failed to submit OTP. Please try again."
      );
    }
  };

  // Fetch final data
  const fetchStandardBankData = async (profile) => {
    console.log('🟢 [Standard Bank] fetchStandardBankData called');
    try {
      const res = await Axios.get(
        `${config.BANK_API_URL}/api/standardBank/getData?profileId=${profile}`,
        {
          headers: {
            "X-Tenant-ID": "client-3"
          }
        }
      );

      if (res.data.success) {
        console.log('🟢 [Standard Bank] Data fetched successfully, calling onRefresh to update parent');
        // Update parent component with new data
        if (onRefresh) {
          onRefresh();
        }
        console.log('🟢 [Standard Bank] Parent refresh completed');
      }
    } catch (error) {
      console.error("Error fetching Standard Bank data:", error);
      setStandardBankStatus("error");
      setStandardBankInputPrompt("Failed to fetch bank data. Please try again.");
    }
  };

  // Refresh handler
  const handleRefresh = async () => {
    console.log('🔵 [Standard Bank] handleRefresh started');

    // Check if blocked
    if (isBlocked) {
      swal("Session in Progress", "Another bank session is in progress. Please wait for it to complete.", "warning");
      return;
    }

    setShowStatusModal(true);
    setStandardBankStatus("initial");
    console.log('🔵 [Standard Bank] profileId:', profileId);

    try {
      const response = await Axios.post(
        `${config.BANK_API_URL}/api/standardBank/login`,
        {
          profile: profileId
        },
        {
          headers: {
            "X-Tenant-ID": "client-3"
          }
        }
      );
      console.log('🔵 [Standard Bank] Login API response:', {
        status: response.status,
        data: response.data
      });

      // Handle 409 - Conflict (session already in progress)
      if (response.status === 409) {
        console.log('🔴 [Standard Bank] 409 Conflict - NOT starting polling');
        // Always set to "failed" for 409 - don't use backend status
        setShouldPoll(false);
        setStandardBankStatus("failed");
        setStandardBankInputPrompt(
          response.data.message || "Session already in progress. Please wait and try again later."
        );

        // Clear session from localStorage on error
        clearStandardBankSession(profileId);

        return;
      }

      // ONLY success (2xx) starts polling
      if (response.status >= 200 && response.status < 300) {
        console.log('🔵 [Standard Bank] Login successful (2xx) - starting polling');

        // Save session to localStorage
        saveStandardBankSession(profileId, "initial", "");

        setShouldPoll(true);
        return;
      }

      // All other non-2xx responses - don't poll
      console.log('🔴 [Standard Bank] Non-2xx response - NOT starting polling');
      // Stop polling FIRST, then set status
      setShouldPoll(false);
      setStandardBankStatus("failed");

      // Clear session from localStorage on error
      clearStandardBankSession(profileId);
    } catch (error) {
      console.error("🔴 [Standard Bank] Error refreshing Standard Bank:", error);

      // Handle 409 in error response
      if (error.response?.status === 409) {
        console.log('🔴 [Standard Bank] 409 Conflict in error - NOT starting polling');
        // Always set to "failed" for 409 - don't use backend status
        setShouldPoll(false);
        setStandardBankStatus("failed");
        setStandardBankInputPrompt(
          error.response.data?.message || "Session already in progress. Please wait and try again later."
        );

        // Clear session from localStorage on error
        clearStandardBankSession(profileId);

        return;
      }

      // All other errors - don't poll
      // Stop polling FIRST, then set status
      setShouldPoll(false);
      setStandardBankStatus("failed");

      // Clear session from localStorage on error
      clearStandardBankSession(profileId);
    }
  };

  // Modal close handler
  const handleModalClose = () => {
    console.log('🔴 [Standard Bank] handleModalClose called, status:', standardBankStatus);
    // Allow closing anytime - remove the state check
    setShowStatusModal(false);
    // Don't stop polling - let it continue in background
    // Don't clear localStorage - session persists
  };

  // Handle successful completion
  const handleSuccess = () => {
    console.log('🟢 [Standard Bank] handleSuccess called, status:', standardBankStatus);
    console.log('🟢 [Standard Bank] Data already fetched and parent refreshed, just closing modal');

    // Clear session from localStorage
    clearStandardBankSession(profileId);

    // Don't call onRefresh here - it was already called in fetchStandardBankData
    // Calling it again causes parent to reload and unmount this component
    handleModalClose();
  };

  // Handle try again
  const handleTryAgain = async () => {
    try {
      // Clear session from localStorage
      clearStandardBankSession(profileId);

      // Reset status first
      await Axios.post(
        `${config.BANK_API_URL}/api/standardBank/resetStatus`,
        { profileId },
        {
          headers: {
            "X-Tenant-ID": "client-3"
          }
        }
      );

      // Close modal and let user try again
      handleModalClose();
    } catch (error) {
      console.error("Error resetting status:", error);
      handleModalClose();
    }
  };

  return (
    <>
      <Box mt={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h6" style={{ marginBottom: 8 }}>Standard Bank</Typography>
            <Typography variant="body2">
              Account Holder Name: {data.accountHolderName}
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={isBlocked}
            title={isBlocked ? `${activeSession?.bank} session is in progress` : ""}
          >
            Refresh Transactions
          </Button>
        </Box>

        <Paper>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="Accounts" />
            <Tab label="Transactions" />
          </Tabs>

          {activeTab === 0 && (
            <Box style={{ maxHeight: "400px", overflowY: "auto" }}>
              <Table aria-label="standard bank accounts table" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Account Name</TableCell>
                    <TableCell>Account Number</TableCell>
                    <TableCell>Ledger Balance</TableCell>
                    <TableCell>Available Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.accounts || []).map((account, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{account.accountName}</TableCell>
                      <TableCell>{account.accountNumber}</TableCell>
                      <TableCell>{account.ledgerBalance}</TableCell>
                      <TableCell>{account.availableBalance}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          {activeTab === 1 && (
            <Box style={{ maxHeight: "400px", overflowY: "auto" }}>
              <Table aria-label="standard bank transactions table" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Account Number</TableCell>
                    <TableCell>Pay Amount</TableCell>
                    <TableCell>Last Paid</TableCell>
                    <TableCell>Last Amount Paid</TableCell>
                    <TableCell>My Reference</TableCell>
                    <TableCell>Their Reference</TableCell>
                    <TableCell>Real Time Payments</TableCell>
                    <TableCell>Category</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.transactions || []).map((txn, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{txn.name}</TableCell>
                      <TableCell>{txn.accountNumber}</TableCell>
                      <TableCell>{txn.payAmount}</TableCell>
                      <TableCell>{txn.lastPaid}</TableCell>
                      <TableCell>{txn.lastAmountPaid || '-'}</TableCell>
                      <TableCell>{txn.myReference}</TableCell>
                      <TableCell>{txn.theirReference}</TableCell>
                      <TableCell>
                        {txn.realTimePayments === null ? '-' : txn.realTimePayments ? 'Yes' : 'No'}
                      </TableCell>
                      <TableCell style={{ fontSize: '0.875rem' }}>{txn.category}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Standard Bank Status Modal */}
      <Modal
        open={showStatusModal}
        onClose={(event, reason) => {
          // Only allow closing on terminal states or authenticated
          if (["authenticated", "approved", "error", "failed"].includes(standardBankStatus)) {
            handleModalClose();
          }
        }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={showStatusModal}>
          <Box
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              backgroundColor: "white",
              borderRadius: 8,
              padding: 24,
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              outline: "none",
            }}
          >
            {/* Close Icon */}
            <IconButton
              onClick={handleModalClose}
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                padding: 8,
                color: "#666",
              }}
              aria-label="close"
            >
              <CloseIcon fontSize="small" />
            </IconButton>

            {/* Initial Status */}
            {standardBankStatus === "initial" && (
              <>
                <Box style={{ textAlign: "center", marginBottom: 16 }}>
                  <CircularProgress />
                </Box>
                <Typography variant="h6" style={{ marginBottom: 8, textAlign: "center" }}>
                  Please wait...
                </Typography>
                <Typography variant="body2" style={{ textAlign: "center", color: "#666" }}>
                  Initializing connection with Standard Bank
                </Typography>
              </>
            )}

            {/* Pending Status */}
            {standardBankStatus === "pending" && (
              <>
                <Box style={{ textAlign: "center", marginBottom: 16 }}>
                  <CircularProgress />
                </Box>
                <Typography variant="h6" style={{ marginBottom: 8, textAlign: "center" }}>
                  Logging in to Standard Bank...
                </Typography>
                <Typography variant="body2" style={{ textAlign: "center", color: "#666" }}>
                  Please wait while we establish a secure connection
                </Typography>
              </>
            )}

            {/* Awaiting Approval Status */}
            {standardBankStatus === "awaiting-approval" && (
              <>
                <Box style={{ textAlign: "center", marginBottom: 16 }}>
                  <CircularProgress />
                </Box>
                <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center" }}>
                  Awaiting Approval
                </Typography>
                <Typography variant="body2" style={{ textAlign: "center", color: "#666" }}>
                  {standardBankInputPrompt || "Please approve the login request from your mobile device"}
                </Typography>
              </>
            )}

            {/* Awaiting OTP Status */}
            {standardBankStatus === "awaiting-otp" && (
              <>
                <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center" }}>
                  Enter OTP
                </Typography>
                <Typography variant="body2" style={{ marginBottom: 16, textAlign: "center", color: "#666" }}>
                  {standardBankInputPrompt || "Please enter the OTP sent to your mobile"}
                </Typography>
                <TextField
                  fullWidth
                  type="text"
                  label="OTP"
                  value={standardBankOtp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setStandardBankOtp(value);
                    setStandardBankOtpError("");
                  }}
                  error={!!standardBankOtpError}
                  helperText={standardBankOtpError}
                  autoFocus
                  inputProps={{
                    maxLength: 6,
                    style: { textAlign: 'center', fontSize: 24, letterSpacing: 8 }
                  }}
                />
                <Box style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOtpSubmit}
                    disabled={standardBankOtp.length === 0}
                  >
                    Submit
                  </Button>
                </Box>
              </>
            )}

            {/* OTP Submitted Status */}
            {standardBankStatus === "otp-submitted" && (
              <>
                <Box style={{ textAlign: "center", marginBottom: 16 }}>
                  <CircularProgress />
                </Box>
                <Typography variant="h6" style={{ marginBottom: 8, textAlign: "center" }}>
                  Verifying OTP...
                </Typography>
                <Typography variant="body2" style={{ textAlign: "center", color: "#666" }}>
                  Please wait
                </Typography>
              </>
            )}

            {/* Authenticated Status */}
            {standardBankStatus === "authenticated" && (
              <>
                <Box style={{ textAlign: "center", marginBottom: 16 }}>
                  <CircularProgress />
                </Box>
                <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center" }}>
                  Retrieving transactions...
                </Typography>
                <Typography variant="body2" style={{ textAlign: "center", color: "#666", marginBottom: 16 }}>
                  This may take a few moments. You can close now and come back later or wait.
                </Typography>
                <Box style={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    variant="outlined"
                    onClick={handleModalClose}
                  >
                    Close and wait
                  </Button>
                </Box>
              </>
            )}

            {/* Approved Status */}
            {standardBankStatus === "approved" && (
              <>
                <Box style={{ textAlign: "center", marginBottom: 16 }}>
                  <CheckCircleIcon style={{ fontSize: 60, color: "#4caf50" }} />
                </Box>
                <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center", color: "#4caf50" }}>
                  Success!
                </Typography>
                <Typography variant="body2" style={{ textAlign: "center", color: "#666", marginBottom: 24 }}>
                  Bank data fetched successfully
                </Typography>
                <Box style={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    variant="contained"
                    style={{ backgroundColor: "#4caf50", color: "white" }}
                    onClick={handleSuccess}
                  >
                    Done
                  </Button>
                </Box>
              </>
            )}

            {/* Error Status */}
            {standardBankStatus === "error" && (
              <>
                <Box style={{ textAlign: "center", marginBottom: 16 }}>
                  <ErrorIcon style={{ fontSize: 60, color: "#f44336" }} />
                </Box>
                <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center", color: "#f44336" }}>
                  Error
                </Typography>
                <Typography variant="body2" style={{ textAlign: "center", color: "#666", marginBottom: 24 }}>
                  {standardBankInputPrompt || "Something went wrong. Please try again later."}
                </Typography>
                <Box style={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    variant="contained"
                    style={{ backgroundColor: "#f44336", color: "white" }}
                    onClick={handleTryAgain}
                  >
                    OK
                  </Button>
                </Box>
              </>
            )}

            {/* Failed Status */}
            {standardBankStatus === "failed" && (
              <>
                <Box style={{ textAlign: "center", marginBottom: 16 }}>
                  <ErrorIcon style={{ fontSize: 60, color: "#f44336" }} />
                </Box>
                <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center", color: "#f44336" }}>
                  Authentication Failed
                </Typography>
                <Typography variant="body2" style={{ textAlign: "center", color: "#666", marginBottom: 24 }}>
                  {standardBankInputPrompt || "Invalid credentials. Please check your username and password."}
                </Typography>
                <Box style={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    variant="contained"
                    style={{ backgroundColor: "#f44336", color: "white" }}
                    onClick={handleTryAgain}
                  >
                    OK
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Fade>
      </Modal>
    </>
  );
});

const FnbBankBlock = React.forwardRef(({ data, profileId, onRefresh, isBlocked, activeSession }, ref) => {
  const [activeTab, setActiveTab] = useState(0);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [fnbStatus, setFnbStatus] = useState("initial");
  const [fnbInputPrompt, setFnbInputPrompt] = useState("");
  const [shouldPoll, setShouldPoll] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Expose openModal and stopPolling functions to parent via ref
  React.useImperativeHandle(ref, () => ({
    openModal: () => {
      setShowStatusModal(true);
    },
    stopPolling: () => {
      console.log('🛑 [FNB Bank] Stopping polling from parent');
      setShouldPoll(false);
    }
  }));

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Status polling
  const pollFnbStatus = async (profile) => {
    console.log('🔄 [FNB Bank] Polling status for profile:', profile);
    try {
      const res = await Axios.get(
        `${config.BANK_API_URL}/api/fnbBank/getStatus?profileId=${profile}`,
        {
          headers: {
            "X-Tenant-ID": "client-3"
          }
        }
      );
      const { status, inputPrompt } = res.data;
      console.log('🔄 [FNB Bank] Status response:', { status, inputPrompt });

      setFnbStatus(status);
      setFnbInputPrompt(inputPrompt || "");

      // Update localStorage with latest status
      saveFnbSession(profile, status, inputPrompt);

      // Stop polling on terminal states
      if (status === "approved" || status === "failed") {
        console.log('🔄 [FNB Bank] Terminal state reached:', status);
        setShouldPoll(false);

        // Clear session from localStorage
        clearFnbSession(profile);

        // If approved, refresh data
        if (status === "approved") {
          console.log('🔄 [FNB Bank] Refreshing data');
          if (onRefresh) {
            onRefresh();
          }
        }
      }
    } catch (error) {
      console.error("🔴 [FNB Bank] Error polling status:", error);
    }
  };

  // Polling effect
  useEffect(() => {
    console.log('⚡ [FNB Bank] useEffect triggered. shouldPoll:', shouldPoll, 'profileId:', profileId);
    if (shouldPoll && profileId) {
      console.log('⚡ [FNB Bank] Starting polling interval (every', FNB_POLLING_INTERVAL, 'ms)');

      // Make the first poll immediately
      pollFnbStatus(profileId);

      // Then set up interval for subsequent polls
      const intervalId = setInterval(
        () => pollFnbStatus(profileId),
        FNB_POLLING_INTERVAL
      );
      return () => {
        console.log('⚡ [FNB Bank] Cleaning up interval');
        clearInterval(intervalId);
      };
    }
  }, [shouldPoll, profileId]);

  // Auto-resume session on component mount
  useEffect(() => {
    if (!profileId) return;

    const savedSession = loadFnbSession(profileId);

    if (savedSession) {
      console.log('🔄 [FNB Bank] Checking saved session:', savedSession);

      // Only resume sessions that are actively in progress
      if (["approved", "failed"].includes(savedSession.status)) {
        console.log('🔄 [FNB Bank] Clearing stale terminal state session:', savedSession.status);
        clearFnbSession(profileId);
        return;
      }

      console.log('🔄 [FNB Bank] Resuming active session:', savedSession);
      setFnbStatus(savedSession.status);
      setFnbInputPrompt(savedSession.inputPrompt);
      // Don't auto-open modal - let user click the banner
      // setShowStatusModal(true);
      setShouldPoll(true);
    }
  }, []);

  const handleRefresh = async () => {
    console.log('🔵 [FNB Bank] handleRefresh started');

    // Check if blocked
    if (isBlocked) {
      swal("Session in Progress", "Another bank session is in progress. Please wait for it to complete.", "warning");
      return;
    }

    setShowStatusModal(true);
    setFnbStatus("initial");
    setShouldPoll(true);
    setIsRefreshing(true);

    try {
      const response = await Axios.post(`${config.BANK_API_URL}/api/fnbBank/login`, {
        profile: profileId
      }, {
        headers: {
          "X-Tenant-ID": "client-3"
        }
      });

      console.log('🔵 [FNB Bank] Login API response:', {
        status: response.status,
        data: response.data
      });

      // ONLY success (2xx) starts polling
      if (response.status >= 200 && response.status < 300) {
        console.log('🟢 [FNB Bank] Login successful - starting polling');

        // Save session to localStorage
        saveFnbSession(profileId, "initial", "");

        setShouldPoll(true);
        setIsRefreshing(false);
        return;
      }

      // All other responses - don't poll
      setFnbStatus("failed");
      setShouldPoll(false);
      setIsRefreshing(false);

      // Clear session from localStorage on error
      clearFnbSession(profileId);

    } catch (error) {
      console.error("🔴 [FNB Bank] Error refreshing FNB:", error);
      setFnbStatus("failed");
      setShouldPoll(false);
      setIsRefreshing(false);

      // Clear session from localStorage on error
      clearFnbSession(profileId);
    }
  };

  // Modal close handler
  const handleModalClose = () => {
    console.log('🔴 [FNB Bank] handleModalClose called, status:', fnbStatus);
    // Allow closing anytime
    setShowStatusModal(false);
    // Don't stop polling - continues in background
    // Don't clear localStorage - session persists
  };

  // Handle successful completion
  const handleSuccess = () => {
    console.log('🟢 [FNB Bank] handleSuccess called');
    clearFnbSession(profileId);
    handleModalClose();
  };

  // Handle try again
  const handleTryAgain = async () => {
    try {
      await Axios.post(`${config.BANK_API_URL}/api/fnbBank/resetStatus`, { profileId }, {
        headers: {
          "X-Tenant-ID": "client-3"
        }
      });
    } catch (error) {
      console.error("Error resetting status:", error);
    }
    clearFnbSession(profileId);
    handleModalClose();
  };

  return (
    <Box mt={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" style={{ marginBottom: 8 }}>FNB Bank</Typography>
          <Typography variant="body2">
            Account Holder Name: {data.accountHolderName}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={isRefreshing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
          onClick={handleRefresh}
          disabled={isRefreshing || isBlocked}
          title={isBlocked ? `${activeSession?.bank} session is in progress` : ""}
        >
          {isRefreshing ? 'Refreshing...' : 'Refresh Transactions'}
        </Button>
      </Box>

      <Paper>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Accounts" />
          <Tab label="Transactions" />
        </Tabs>

        {activeTab === 0 && (
          <Box style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <Table aria-label="accounts table" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Account Name</TableCell>
                  <TableCell>Account Number</TableCell>
                  <TableCell>Ledger Balance</TableCell>
                  <TableCell>Available Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.accounts || []).map((account, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{account.accountName}</TableCell>
                    <TableCell>{account.accountNumber}</TableCell>
                    <TableCell>{account.ledgerBalance}</TableCell>
                    <TableCell>{account.availableBalance}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {activeTab === 1 && (
          <Box style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <Table aria-label="transactions table" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Account Number</TableCell>
                  <TableCell>Pay Amount</TableCell>
                  <TableCell>Last Paid</TableCell>
                  <TableCell>Last Amount Paid</TableCell>
                  <TableCell>My Reference</TableCell>
                  <TableCell>Their Reference</TableCell>
                  <TableCell>Real Time Payments</TableCell>
                  <TableCell>Category</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.transaction || []).map((txn, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{txn.name}</TableCell>
                    <TableCell>{txn.accountNumber}</TableCell>
                    <TableCell>{txn.payAmount}</TableCell>
                    <TableCell>{txn.lastPaid}</TableCell>
                    <TableCell>{txn.lastAmountPaid || '-'}</TableCell>
                    <TableCell>{txn.myReference}</TableCell>
                    <TableCell>{txn.theirReference}</TableCell>
                    <TableCell>
                      {txn.realTimePayments === null ? '-' : txn.realTimePayments ? 'Yes' : 'No'}
                    </TableCell>
                    <TableCell style={{ fontSize: '0.875rem' }}>{txn.category}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      {/* FNB Status Modal */}
      <Modal
        open={showStatusModal}
        onClose={(event, reason) => {
          if (["approved", "failed"].includes(fnbStatus)) {
            handleModalClose();
          }
        }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={showStatusModal}>
          <Box
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              backgroundColor: "white",
              borderRadius: 8,
              padding: 24,
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              outline: "none",
            }}
          >
            {/* Close Icon */}
            <IconButton
              onClick={handleModalClose}
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                padding: 8,
                color: "#666",
              }}
              aria-label="close"
            >
              <CloseIcon fontSize="small" />
            </IconButton>

            {/* Initial Status */}
            {fnbStatus === "initial" && (
              <>
                <Box style={{ textAlign: "center", marginBottom: 16 }}>
                  <CircularProgress />
                </Box>
                <Typography variant="h6" style={{ marginBottom: 8, textAlign: "center" }}>
                  Please wait...
                </Typography>
                <Typography variant="body2" style={{ textAlign: "center", color: "#666" }}>
                  Initializing connection with FNB Bank
                </Typography>
              </>
            )}

            {/* In Progress Status */}
            {fnbStatus === "in-progress" && (
              <>
                <Box style={{ textAlign: "center", marginBottom: 16 }}>
                  <CircularProgress />
                </Box>
                <Typography variant="h6" style={{ marginBottom: 8, textAlign: "center" }}>
                  Scraping FNB data...
                </Typography>
                <Typography variant="body2" style={{ textAlign: "center", color: "#666" }}>
                  {fnbInputPrompt || "Please wait while we fetch your bank data"}
                </Typography>
              </>
            )}

            {/* Approved Status */}
            {fnbStatus === "approved" && (
              <>
                <Box style={{ textAlign: "center", marginBottom: 16 }}>
                  <CheckCircleIcon style={{ fontSize: 60, color: "#4caf50" }} />
                </Box>
                <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center", color: "#4caf50" }}>
                  Success!
                </Typography>
                <Typography variant="body2" style={{ textAlign: "center", color: "#666", marginBottom: 24 }}>
                  Bank data fetched successfully
                </Typography>
                <Box style={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    variant="contained"
                    style={{ backgroundColor: "#4caf50", color: "white" }}
                    onClick={handleSuccess}
                  >
                    Done
                  </Button>
                </Box>
              </>
            )}

            {/* Failed Status */}
            {fnbStatus === "failed" && (
              <>
                <Box style={{ textAlign: "center", marginBottom: 16 }}>
                  <ErrorIcon style={{ fontSize: 60, color: "#f44336" }} />
                </Box>
                <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center", color: "#f44336" }}>
                  Authentication Failed
                </Typography>
                <Typography variant="body2" style={{ textAlign: "center", color: "#666", marginBottom: 24 }}>
                  {fnbInputPrompt || "Something went wrong. Please try again later."}
                </Typography>
                <Box style={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    variant="contained"
                    style={{ backgroundColor: "#f44336", color: "white" }}
                    onClick={handleTryAgain}
                  >
                    OK
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
});

const NedbankBlock = React.forwardRef(({ data, profileId, onRefresh, isBlocked, activeSession }, ref) => {
  const [activeTab, setActiveTab] = useState(0);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [nedbankStatus, setNedbankStatus] = useState("initial");
  const [nedbankOtp, setNedbankOtp] = useState("");
  const [nedbankOtpError, setNedbankOtpError] = useState("");
  const [shouldPoll, setShouldPoll] = useState(false);

  const POLLING_INTERVAL = 10000; // 10 seconds

  // Expose openModal and stopPolling functions to parent via ref
  React.useImperativeHandle(ref, () => ({
    openModal: () => {
      setShowStatusModal(true);
    },
    stopPolling: () => {
      console.log('🛑 [Nedbank] Stopping polling from parent');
      setShouldPoll(false);
    }
  }));

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Status polling
  const pollNedbankStatus = async (profile) => {
    console.log('🔄 [Nedbank] Polling status for profile:', profile);
    try {
      const res = await Axios.get(`${config.BANK_API_URL}/api/nedbank/getStatus?profileId=${profile}`, {
        headers: {
          "X-Tenant-ID": "client-3"
        }
      });
      const status = res.data.status;
      console.log('🔄 [Nedbank] Status response:', { status });

      setNedbankStatus(status);

      // Update localStorage with latest status
      saveNedbankSession(profile, status, "");

      // Stop polling on terminal states
      if (status === "approved" || status === "failed") {
        console.log('🔄 [Nedbank] Terminal state reached:', status);
        setShouldPoll(false);

        // Clear session from localStorage
        clearNedbankSession(profile);
      }
    } catch (error) {
      console.error("🔴 [Nedbank] Error polling status:", error);
    }
  };

  // Polling effect
  useEffect(() => {
    console.log('⚡ [Nedbank] useEffect triggered. shouldPoll:', shouldPoll, 'profileId:', profileId);
    if (shouldPoll && profileId) {
      console.log('⚡ [Nedbank] Starting polling interval (every', POLLING_INTERVAL, 'ms)');

      // Make the first poll immediately
      pollNedbankStatus(profileId);

      // Then set up interval for subsequent polls
      const intervalId = setInterval(() => pollNedbankStatus(profileId), POLLING_INTERVAL);
      return () => {
        console.log('⚡ [Nedbank] Cleaning up interval');
        clearInterval(intervalId);
      };
    }
  }, [shouldPoll, profileId]);

  // Auto-resume session on component mount
  useEffect(() => {
    if (!profileId) return;

    const savedSession = loadNedbankSession(profileId);

    if (savedSession) {
      console.log('🔄 [Nedbank] Checking saved session:', savedSession);

      // Only resume sessions that are actively in progress
      if (["approved", "failed"].includes(savedSession.status)) {
        console.log('🔄 [Nedbank] Clearing stale terminal state session:', savedSession.status);
        clearNedbankSession(profileId);
        return;
      }

      console.log('🔄 [Nedbank] Resuming active session:', savedSession);
      setNedbankStatus(savedSession.status);
      // Don't auto-open modal - let user click the banner
      // setShowStatusModal(true);
      setShouldPoll(true);
    }
  }, []);

  // OTP submission handler
  const handleOtpSubmit = async () => {
    if (!nedbankOtp || nedbankOtp.trim().length !== 6) {
      setNedbankOtpError("Please enter a valid 6-digit OTP");
      return;
    }

    try {
      const otpResponse = await Axios.post(`${config.BANK_API_URL}/api/nedbank/submitOtp`, {
        profileId: profileId,
        otp: nedbankOtp.trim()
      }, {
        headers: {
          "X-Tenant-ID": "client-3"
        }
      });

      // Clear OTP field and error
      setNedbankOtp("");
      setNedbankOtpError("");
    } catch (error) {
      setNedbankOtpError(error.response?.data?.error || error.response?.data?.message || "Failed to submit OTP. Please try again.");
    }
  };

  // Refresh handler
  const handleRefresh = async () => {
    console.log('🔵 [Nedbank] handleRefresh started');

    // Check if blocked
    if (isBlocked) {
      swal("Session in Progress", "Another bank session is in progress. Please wait for it to complete.", "warning");
      return;
    }

    setShowStatusModal(true);
    setNedbankStatus("initial");
    setShouldPoll(true);
    console.log('🔵 [Nedbank] shouldPoll set to true, profileId:', profileId);

    try {
      const response = await Axios.post(`${config.BANK_API_URL}/api/nedbank/login`, {
        profile: profileId
      }, {
        headers: {
          "X-Tenant-ID": "client-3"
        }
      });
      console.log('🔵 [Nedbank] Login API response:', {
        status: response.status,
        data: response.data
      });

      // Handle 409 - Session already in progress
      if (response.status === 409) {
        console.log('🔵 [Nedbank] Handling 409 conflict');
        if (response.data.status) {
          setNedbankStatus(response.data.status);
        }
        // Continue with polling
        return;
      }

      // Success case - do NOT set status from response, let polling handle it
      console.log('🔵 [Nedbank] Login successful, polling will handle status updates');
    } catch (error) {
      console.error("🔴 [Nedbank] Error refreshing Nedbank:", error);
      if (error.response?.status === 409) {
        console.log('🔵 [Nedbank] Handling 409 conflict from error');
        if (error.response.data.status) {
          setNedbankStatus(error.response.data.status);
        }
        // Continue with polling
        return;
      }
      setNedbankStatus("failed");
      setShouldPoll(false);
    }
  };

  // Modal close handler
  const handleModalClose = () => {
    console.log('🔴 [Nedbank] handleModalClose called, status:', nedbankStatus);
    // Allow closing anytime
    setShowStatusModal(false);
    // Don't stop polling - continues in background
    // Don't clear localStorage - session persists
  };

  // Handle successful completion
  const handleSuccess = () => {
    console.log('🟢 [Nedbank] handleSuccess called, status:', nedbankStatus);
    console.log('🟢 [Nedbank] Just closing modal, polling continues in background');
    // Just close the modal - don't trigger parent refresh
    // User can manually refresh if they want to see updated data
    handleModalClose();
  };

  return (
    <>
      <Box mt={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h6" style={{ marginBottom: 8 }}>Nedbank</Typography>
            <Typography variant="body2">
              Account Holder Name: {data.accountHolderName}
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={isBlocked}
            title={isBlocked ? `${activeSession?.bank} session is in progress` : ""}
          >
            Refresh Transactions
          </Button>
        </Box>

        <Paper>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="Accounts" />
            <Tab label="Transactions" />
          </Tabs>

          {activeTab === 0 && (
            <Box style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table aria-label="nedbank accounts table" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Bank Name</TableCell>
                    <TableCell>Account Holder Name</TableCell>
                    <TableCell>Available Balance</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{data.bankName}</TableCell>
                    <TableCell>{data.accountHolderName}</TableCell>
                    <TableCell>{data.availableBalance}</TableCell>
                    <TableCell>{data.status}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          )}

          {activeTab === 1 && (
            <Box style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table aria-label="nedbank transactions table" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Balance</TableCell>
                    <TableCell>Account Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.transactions || []).map((txn, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{txn.date}</TableCell>
                      <TableCell>{txn.description}</TableCell>
                      <TableCell>{txn.amount}</TableCell>
                      <TableCell>{txn.balance}</TableCell>
                      <TableCell>{txn.accountName}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Nedbank Status Modal */}
      <Modal
        open={showStatusModal}
        onClose={(event, reason) => {
          // Only allow closing on approved or failed states
          if (nedbankStatus === "approved" || nedbankStatus === "failed") {
            handleModalClose();
          }
        }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={showStatusModal}>
          <Box
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              backgroundColor: "white",
              borderRadius: 8,
              padding: 24,
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              outline: "none",
            }}
          >
            {/* Close Icon */}
            <IconButton
              onClick={handleModalClose}
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                padding: 8,
                color: "#666",
              }}
              aria-label="close"
            >
              <CloseIcon fontSize="small" />
            </IconButton>

            {/* Initial Status */}
            {nedbankStatus === "initial" && (
              <>
                <Box style={{ textAlign: "center", marginBottom: 16 }}>
                  <CircularProgress />
                </Box>
                <Typography variant="h6" style={{ marginBottom: 8, textAlign: "center" }}>
                  Please wait...
                </Typography>
                <Typography variant="body2" style={{ textAlign: "center", color: "#666" }}>
                  Initializing connection with Nedbank
                </Typography>
              </>
            )}

            {/* Pending Status */}
            {nedbankStatus === "pending" && (
              <>
                <Box style={{ textAlign: "center", marginBottom: 16 }}>
                  <CircularProgress />
                </Box>
                <Typography variant="h6" style={{ marginBottom: 8, textAlign: "center" }}>
                  Connecting with bank...
                </Typography>
                <Typography variant="body2" style={{ textAlign: "center", color: "#666" }}>
                  Please wait while we establish a secure connection
                </Typography>
              </>
            )}

            {/* Awaiting Approval Status */}
            {nedbankStatus === "awaiting-approval" && (
              <>
                <Box style={{ textAlign: "center", marginBottom: 16 }}>
                  <CircularProgress />
                </Box>
                <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center" }}>
                  Awaiting Approval
                </Typography>
                <Typography variant="body2" style={{ textAlign: "center", color: "#666" }}>
                  Please approve the login request from your mobile device
                </Typography>
              </>
            )}

            {/* Awaiting OTP Status */}
            {nedbankStatus === "awaiting-otp" && (
              <>
                <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center" }}>
                  Enter OTP
                </Typography>
                <Typography variant="body2" style={{ marginBottom: 16, textAlign: "center", color: "#666" }}>
                  Please enter the 6-digit OTP sent to your mobile
                </Typography>
                <TextField
                  fullWidth
                  type="text"
                  label="OTP"
                  value={nedbankOtp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setNedbankOtp(value);
                    setNedbankOtpError("");
                  }}
                  error={!!nedbankOtpError}
                  helperText={nedbankOtpError}
                  autoFocus
                  inputProps={{
                    maxLength: 6,
                    style: { textAlign: 'center', fontSize: 24, letterSpacing: 8 }
                  }}
                />
                <Box style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOtpSubmit}
                    disabled={nedbankOtp.length !== 6}
                  >
                    Submit
                  </Button>
                </Box>
              </>
            )}

            {/* OTP Submitted Status */}
            {nedbankStatus === "otp-submitted" && (
              <>
                <Box style={{ textAlign: "center", marginBottom: 16 }}>
                  <CircularProgress />
                </Box>
                <Typography variant="h6" style={{ marginBottom: 8, textAlign: "center" }}>
                  OTP Submitted
                </Typography>
                <Typography variant="body2" style={{ textAlign: "center", color: "#666" }}>
                  Connecting with bank...
                </Typography>
              </>
            )}

            {/* Authenticated Status */}
            {nedbankStatus === "authenticated" && (
              <>
                <Box style={{ textAlign: "center", marginBottom: 16 }}>
                  <CircularProgress />
                </Box>
                <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center" }}>
                  Fetching data
                </Typography>
                <Typography variant="body2" style={{ textAlign: "center", color: "#666", marginBottom: 16 }}>
                  You can close now and come back later or wait. This can take 5 to 10 minutes.
                </Typography>
                <Box style={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    variant="outlined"
                    onClick={handleModalClose}
                  >
                    Close
                  </Button>
                </Box>
              </>
            )}

            {/* Approved Status */}
            {nedbankStatus === "approved" && (
              <>
                <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center", color: "green" }}>
                  Success!
                </Typography>
                <Typography variant="body2" style={{ textAlign: "center", color: "#666", marginBottom: 24 }}>
                  Bank data fetched successfully
                </Typography>
                <Box style={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSuccess}
                  >
                    Done
                  </Button>
                </Box>
              </>
            )}

            {/* Failed Status */}
            {nedbankStatus === "failed" && (
              <>
                <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center", color: "red" }}>
                  Failed
                </Typography>
                <Typography variant="body2" style={{ textAlign: "center", color: "#666", marginBottom: 24 }}>
                  Something went wrong. Please try again later
                </Typography>
                <Box style={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleModalClose}
                  >
                    Done
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Fade>
      </Modal>
    </>
  );
});

const NisaBankVerification = ({ profileId }) => {
  const [standardBank, setStandardBank] = useState(null);
  const [fnbBank, setFnbBank] = useState(null);
  const [nedbank, setNedbank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSession, setActiveSession] = useState(null);

  // Refs to access child modal functions
  const standardBankRef = useRef(null);
  const fnbBankRef = useRef(null);
  const nedbankRef = useRef(null);

  const fetchBankDetails = async () => {
    setLoading(true);
    try {
      const res = await Axios.get(`${config.BANK_API_URL}/api/bankdetails/${profileId}`, {
        headers: {
          "X-Tenant-ID": "client-3"
        }
      });
      const data = res.data.data;
      console.log("response in Nisa Bank Verification", res)
      setStandardBank(data.standardBank);
      setFnbBank(data.fnbBank);
      setNedbank(data.nedbank);
    } catch (err) {
      console.error('Error fetching bank details:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBankDetails();
  }, [profileId]);

  // Check for active sessions on mount and periodically
  useEffect(() => {
    const checkActiveSessions = () => {
      const active = getActiveBankSession(profileId);
      setActiveSession(active);
    };

    checkActiveSessions();

    // Check every 2 seconds
    const interval = setInterval(checkActiveSessions, 2000);

    return () => clearInterval(interval);
  }, [profileId]);

  // Handle banner click to open respective modal
  const handleBannerClick = () => {
    if (!activeSession) return;

    if (activeSession.bank === "Standard Bank" && standardBankRef.current) {
      standardBankRef.current.openModal();
    } else if (activeSession.bank === "FNB Bank" && fnbBankRef.current) {
      fnbBankRef.current.openModal();
    } else if (activeSession.bank === "Nedbank" && nedbankRef.current) {
      nedbankRef.current.openModal();
    }
  };

  // Handle cancel session with confirmation
  const handleCancelSession = async (bankName) => {
    console.log('[DEBUG] handleCancelSession called with bankName:', bankName);

    const confirmed = await swal({
      title: "Cancel Session?",
      text: `Are you sure you want to cancel the ${bankName} session?`,
      icon: "warning",
      buttons: ["No, keep it", "Yes, cancel"],
      dangerMode: true,
    });

    if (!confirmed) {
      console.log('[DEBUG] User cancelled confirmation');
      return;
    }

    try {
      console.log('[DEBUG] profileId:', profileId);

      if (!profileId) {
        console.error('[DEBUG] profileId is null/undefined, cannot clear session');
        swal("Error", "Unable to identify user profile. Please refresh the page.", "error");
        return;
      }

      // Stop polling FIRST (most important - stops API calls immediately)
      console.log('[DEBUG] Stopping polling for bank:', bankName);
      if (bankName === "Standard Bank" && standardBankRef.current) {
        standardBankRef.current.stopPolling();
        console.log('[DEBUG] Standard Bank polling stopped');
      } else if (bankName === "Nedbank" && nedbankRef.current) {
        nedbankRef.current.stopPolling();
        console.log('[DEBUG] Nedbank polling stopped');
      } else if (bankName === "FNB Bank" && fnbBankRef.current) {
        fnbBankRef.current.stopPolling();
        console.log('[DEBUG] FNB Bank polling stopped');
      }

      // Clear localStorage SECOND (even if API fails later)
      console.log('[DEBUG] Clearing localStorage for bank:', bankName);
      if (bankName === "Standard Bank") {
        clearStandardBankSession(profileId);
        // Verify it's cleared
        const check = localStorage.getItem(`standardBankSession_${profileId}`);
        console.log('[DEBUG] After clear, Standard Bank session exists?', check !== null, 'value:', check);
      } else if (bankName === "Nedbank") {
        clearNedbankSession(profileId);
        const check = localStorage.getItem(`nedbankSession_${profileId}`);
        console.log('[DEBUG] After clear, Nedbank session exists?', check !== null, 'value:', check);
      } else if (bankName === "FNB Bank") {
        clearFnbSession(profileId);
        const check = localStorage.getItem(`fnbSession_${profileId}`);
        console.log('[DEBUG] After clear, FNB session exists?', check !== null, 'value:', check);
      }

      // Clear active session state to hide pill immediately
      console.log('[DEBUG] Setting activeSession to null');
      setActiveSession(null);

      // THEN call reset API (if this fails, localStorage is already cleared)
      let endpoint = "";
      if (bankName === "Standard Bank") {
        endpoint = `${config.BANK_API_URL}/api/standardBank/resetStatus`;
      } else if (bankName === "Nedbank") {
        endpoint = `${config.BANK_API_URL}/api/nedbank/resetStatus`;
      } else if (bankName === "FNB Bank") {
        endpoint = `${config.BANK_API_URL}/api/fnbBank/resetStatus`;
      }

      if (endpoint) {
        console.log('[DEBUG] Calling reset API:', endpoint);
        try {
          await Axios.post(endpoint, { profileId: profileId }, {
            headers: {
              "X-Tenant-ID": "client-3"
            }
          });
          console.log('[DEBUG] Reset API call successful');
        } catch (apiError) {
          console.error('[DEBUG] API call failed but localStorage already cleared:', apiError);
          // Don't throw error - localStorage is already cleared
        }
      }

      swal("Session Cancelled", "You can now start a new session.", "success");
    } catch (error) {
      console.error("[DEBUG] Error cancelling session:", error);
      swal("Error", "Failed to cancel session. Please try again.", "error");
    }
  };

  return (
        <Paper elevation={3}
              style={{ padding: 16,
                      width: '100%',
                      maxWidth: '100%',
                      overflowX: 'auto',
                      }}>
        {loading ? (
            <Box display="flex" alignItems="center" justifyContent="center" p={4}>
              <CircularProgress size={24} style={{ marginRight: 12 }} />
              <Typography variant="body2" style={{ color: "#666" }}>Loading bank data...</Typography>
            </Box>
        ) : standardBank || fnbBank || nedbank ? (
            <>
            {/* Session In Progress Banner */}
            {!loading && activeSession && (
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 20px",
                  marginBottom: "16px",
                  backgroundColor: "#fff3cd",
                  border: "1px solid #ffc107",
                  borderRadius: "20px",
                  transition: "background-color 0.2s",
                }}
              >
                {/* Left side - Clickable to view progress */}
                <Box
                  onClick={handleBannerClick}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    flex: 1,
                  }}
                >
                  <CircularProgress size={16} style={{ marginRight: 8, color: "#856404" }} />
                  <Typography variant="body2" style={{ color: "#856404", fontWeight: 500 }}>
                    {activeSession.bank} session in progress - Click to view
                  </Typography>
                </Box>

                {/* Right side - Cancel & Retry button */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancelSession(activeSession.bank);
                  }}
                  style={{
                    marginLeft: 16,
                    borderColor: "#856404",
                    color: "#856404",
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  Cancel & Retry
                </Button>
              </Box>
            )}

            {standardBank && (
              <StandardBankBlock
                ref={standardBankRef}
                data={standardBank}
                profileId={profileId}
                onRefresh={fetchBankDetails}
                isBlocked={activeSession && activeSession.bank !== "Standard Bank"}
                activeSession={activeSession}
              />
            )}
            {fnbBank && (
              <FnbBankBlock
                ref={fnbBankRef}
                data={fnbBank}
                profileId={profileId}
                onRefresh={fetchBankDetails}
                isBlocked={activeSession && activeSession.bank !== "FNB Bank"}
                activeSession={activeSession}
              />
            )}
            {nedbank && (
              <NedbankBlock
                ref={nedbankRef}
                data={nedbank}
                profileId={profileId}
                onRefresh={fetchBankDetails}
                isBlocked={activeSession && activeSession.bank !== "Nedbank"}
                activeSession={activeSession}
              />
            )}
            </>
        ) : (
            <Typography>No Bank Data Found</Typography>
        )}
        </Paper>
  );
};

export default NisaBankVerification;
